import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import PDFViewer from './PDFViewer';
import { documentAPI, fieldApi, handleAPIError } from './LibraryAPI';
import { getDocMetadata } from './documentMetadata';
import './PDFViewer.css';

const DocumentViewer = () => {
  console.log('[ROUTE DEBUG] libraryViewer/DocumentViewer component loaded');
  const { documentId } = useParams();
  const [document, setDocument] = useState(null);
  const [unifiedFields, setUnifiedFields] = useState({ definitions: [], valuesByKey: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadDocument = useCallback(async () => {
    if (!documentId) return;

    setLoading(true);
    setError(null);

    try {
      console.log('[DocumentViewer] Loading document:', documentId);
      
      // Load document data (same as working library)
      console.log('[API DEBUG] libraryViewer/DocumentViewer calling documentAPI.getDocument');
      const freshDoc = await documentAPI.getDocument(documentId);
      const merged = { ...freshDoc, metadata: getDocMetadata(freshDoc) };
      setDocument(merged);
      console.log('[DocumentViewer] Document loaded:', merged);
      
      // Load unified fields (same as working library)
      try {
        const fieldResult = await fieldApi.getDocumentFields(String(documentId));
        setUnifiedFields(fieldResult);
        console.log('[DocumentViewer] Unified fields loaded:', fieldResult);
      } catch (fieldErr) {
        console.warn('[DocumentViewer] Failed to load unified fields:', fieldErr);
        // Continue without fields - document can still be viewed
      }
      
    } catch (err) {
      const errorInfo = handleAPIError(err, 'Load Document');
      
      // Add helpful message for 401 errors
      if (err?.response?.status === 401) {
        errorInfo.helpMessage = 'Your session has expired or lacks access to this document. Sign in again and retry.';
      }
      
      setError(errorInfo);
      console.error('[DocumentViewer] Load failed:', errorInfo);
    } finally {
      setLoading(false);
    }
  }, [documentId]);

  useEffect(() => {
    loadDocument();
  }, [loadDocument]);

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        flexDirection: 'column'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '4px solid #f3f3f3',
          borderTop: '4px solid #007cba',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          marginBottom: '16px'
        }}></div>
        <p>Loading document...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        flexDirection: 'column',
        padding: '2rem'
      }}>
        <div style={{
          backgroundColor: '#fee2e2',
          border: '1px solid #ef4444',
          borderRadius: '0.5rem',
          padding: '1rem',
          marginBottom: '1rem',
          textAlign: 'left',
          maxWidth: '500px'
        }}>
          <p><strong>Error:</strong> {error.message}</p>
          {error.status && <p><strong>Status:</strong> {error.status}</p>}
          {error.type && <p><strong>Type:</strong> {error.type}</p>}
          {error.helpMessage && (
            <p style={{ marginTop: '0.5rem', fontStyle: 'italic', color: '#b91c1c' }}>
              <strong>Help:</strong> {error.helpMessage}
            </p>
          )}
        </div>
        <button
          onClick={loadDocument}
          style={{
            padding: '8px 16px',
            backgroundColor: '#007cba',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          🔄 Retry
        </button>
      </div>
    );
  }

  if (!document) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>No document found</p>
      </div>
    );
  }

  return (
    <div style={{ height: '100vh' }}>
      <PDFViewer document={document} />
    </div>
  );
};

export default DocumentViewer;
